import { Injectable } from '@angular/core';

import { OddsOperations, emptyCalculatedOdds, emptyOdds } from '@frontend/sports/odds/feature';
import { groupBy } from 'lodash-es';

import { BetslipPick } from '../../../core/picks/betslip-pick';
import { IBasePrice, PriceType } from '../../../core/picks/pick-models';
import { getEachWayOdds } from '../../picks/services/betslip-pick-helpers';
import { IRewardToken } from '../../reward-tokens/reward-tokens.model';
import {
    BetType,
    CombinationRestrictions,
    EachWayInfo,
    IBet,
    IBetLeg,
    IBetslip,
    IBetslipCalculatorOverrides,
    IBetslipCalculatorSlipOverride,
    ICalculatedPrice,
    ICalculatorInput,
    ISlip,
    ISlipPick,
    PickInfo,
    emptyBetslipCalculatorOverrides,
    emptyCombinationRestrictions,
} from '../models';
import { SingleSlipType, SlipType } from '../slip-type';
import BetslipCombinator from './betslip-combinator';

/**
 * This class generate the a betslip (list of slips and bets ) which user is going to place.
 */
@Injectable()
export class BetslipGeneratorService {
    private readonly combinator = new BetslipCombinator();

    calculate(
        input: ICalculatorInput,
        options: {
            overrides?: IBetslipCalculatorOverrides;
            restrictions?: CombinationRestrictions;
        } = { overrides: emptyBetslipCalculatorOverrides, restrictions: emptyCombinationRestrictions },
    ): IBetslip {
        const overrides = options.overrides || emptyBetslipCalculatorOverrides;
        const restrictions = options.restrictions || emptyCombinationRestrictions;

        const output: IBetslip = {
            slips: [] as ISlip[],
        };

        let slipIndex = 1;
        for (const slipInfo of input.betslip) {
            const slipItems = [...slipInfo.items, ...Object.values(overrides.pickOverrides.additions || {})]
                .filter((p) => {
                    const pickId = p.pick.id.toString();
                    if (typeof overrides.pickOverrides.replacements === 'undefined') {
                        // If no overrides no filter
                        return true;
                    }
                    if (overrides.pickOverrides.replacements[pickId] === null) {
                        // If pick is override with value null then we filter it.
                        return false;
                    }

                    return true;
                })
                .map((p) => this.getSlipPick(p, overrides));
            if (!slipItems.length) {
                continue;
            }

            const slip: ISlip = {
                slipId: slipInfo.slipId,
                slipPicks: slipItems.reduce(
                    (aggr, si) => {
                        aggr[si.pick.id.toString()] = si;

                        return aggr;
                    },
                    {} as Record<string, ISlipPick<BetslipPick>>,
                ),
                bets: [] as IBet[],
                rewardToken: this.getSlipRewardToken(slipItems, slipInfo.system, slipInfo.rewardToken, overrides),
                type: slipInfo.system,
                stake: this.getSlipStake(slipItems, slipInfo.system, slipInfo.stake, overrides),
                hasError: false,
                index: slipIndex,
            };

            const [bankers, normal] = slipItems.reduce(
                (aggr, si) => {
                    if (si.isBanker) {
                        aggr[0].push(si);
                    } else {
                        aggr[1].push(si);
                    }

                    return aggr;
                },
                [[], []] as [ISlipPick[], ISlipPick[]],
            );

            //splitBy(slipItems, i => i.isBanker);
            const isEachWaySlip = slipItems.some((s) => !!s.eachWayInfo);
            const combinations = this.combinator.combine(normal, slipInfo.system.basis, bankers);

            let hasInvalidCombination = false;
            for (let i = 0; i < combinations.length; i++) {
                const combination = combinations[i];
                // A combination of picks can create multiple bets, if EW is enabled for any of the picks.
                const isValid = this.isValidCombination(combination, restrictions, slipInfo.system.basis);
                if (!isValid) {
                    hasInvalidCombination = true;
                }
                for (const bet of this.createBets(combination, isEachWaySlip, i, isValid)) {
                    slip.bets.push(bet);
                }
            }

            slip.hasError = slipInfo.hasError || hasInvalidCombination;
            this.updateSlipByBetsStatus(slip, restrictions);

            output.slips.push(slip);
            slipIndex++;
        }

        return output;
    }

    private isValidCombination(combination: ISlipPick[], restrictions: CombinationRestrictions, slipTypeBasis: number[]): boolean {
        // if there are no restrictions, there is nothing that can fail
        if (restrictions === emptyCombinationRestrictions) {
            return true;
        }

        // exclude all "named" systems (but why?)
        if (slipTypeBasis.length < 2) {
            return true;
        }
        for (const slipPick of combination) {
            const pickId = slipPick.pick.id.toString();
            if (
                pickId in restrictions.notCombinablePicks &&
                combination.some((sp1) => sp1.pick.id.toString() in restrictions.notCombinablePicks[pickId])
            ) {
                return false;
            }
            if (pickId in restrictions.minimumCombinations && slipTypeBasis.some((b) => b < restrictions.minimumCombinations[pickId])) {
                return false;
            }
        }

        return true;
    }

    /**
     * Get Real pick info after applying overrides.
     *
     * @param pickInfo
     * @param overrides
     * @private
     */
    private getSlipPick(pickInfo: PickInfo, overrides: IBetslipCalculatorOverrides): ISlipPick {
        const realPickInfo = this.getPickInfo(pickInfo, overrides); // Get pick info if has overrides then use them.
        const eachWayInfo = this.getEachWayTerms(realPickInfo, overrides); // Get if there is a each way override.
        const price = this.getPickPrice(realPickInfo, false, overrides); // We want the pick price after overrides.

        return {
            pick: realPickInfo.pick,
            eachWayInfo,
            price,
            isBanker: realPickInfo.isBanker,
            priceIds: realPickInfo.priceIds,
            isXCast: realPickInfo.isXCast,
            isStartingPrice: realPickInfo.isStartingPrice,
        };
    }

    /**
     * In order to create slip we need the bets in it. If there are picks which are EW, we create 2 bets.
     * If pick is combinational 2/3 cast then we create 1 bet with bet count greater than 1
     *
     * @param combination
     * @param isEachWaySlip is the slip marked as EW
     * @param combinationIndex combination index
     * @param isValid is bet valid
     * @private
     */
    private *createBets(combination: ISlipPick[], isEachWaySlip: boolean, combinationIndex: number, isValid: boolean): Iterable<IBet> {
        yield this.createBet(combination, false, combinationIndex, isValid);
        if (isEachWaySlip) {
            // For systems when system is marked as EW but a combination has no EW picks we create a bet with EW false.
            yield this.createBet(combination, true, undefined, isValid);
        }
    }

    private createBet(combination: ISlipPick[], isEachWaySlip: boolean, combinationIndex: number | undefined, isValid: boolean): IBet {
        const legs: IBetLeg[] = [];
        let betCount = 0;
        for (const pickInfo of combination) {
            legs.push(this.createLeg(pickInfo, isEachWaySlip));
            if (betCount === 0) {
                betCount = pickInfo.pick.betCount();
            } else {
                betCount *= pickInfo.pick.betCount();
            }
        }

        return {
            legs,
            betCount,
            betType:
                betCount > 1 ? BetType.Combinational : isEachWaySlip && combination.some((l) => !!l.eachWayInfo) ? BetType.EachWay : BetType.Normal,
            index: combinationIndex,
            isValid,
        };
    }

    private createLeg(pickInfo: ISlipPick, isEachWaySlip: boolean): IBetLeg {
        const leg: IBetLeg = {
            pickId: pickInfo.pick.id,
            price:
                isEachWaySlip && pickInfo.eachWayInfo
                    ? this.getEachWayPrice(pickInfo)
                    : pickInfo.pick.boostedPriceDetails?.isPriceBoosted
                      ? this.getBoostedPrice(pickInfo)
                      : { type: pickInfo.price.type, odds: OddsOperations.toCalculatedOdds(pickInfo.price.nativeOdds) },
        };

        return leg;
    }

    private getBoostedPrice(pickInfo: ISlipPick): ICalculatedPrice {
        return {
            type: pickInfo.price.type,
            odds: OddsOperations.toCalculatedOdds(pickInfo.pick.boostedPriceDetails?.boostedPrice?.nativeOdds),
        };
    }

    private getBetOverride(
        combination: ISlipPick[],
        system: SlipType,
        overrides: IBetslipCalculatorOverrides,
    ): IBetslipCalculatorSlipOverride | undefined {
        return overrides.slipOverrides.find(
            (b) =>
                b.system.equals(system) &&
                (!(system instanceof SingleSlipType) || // For now we will check picks only for singles
                    combination[0].pick.id.isEqual(b.pickIds[0])), // For singles we have only one pick so the check is simpler.
        );
    }

    private getSlipStake(
        combination: ISlipPick[],
        system: SlipType,
        originalStake: number | null,
        overrides: IBetslipCalculatorOverrides,
    ): number | null {
        const betOverride = this.getBetOverride(combination, system, overrides);
        if (betOverride?.stake) {
            return betOverride.stake.value;
        } else {
            return originalStake;
        }
    }

    private getSlipRewardToken(
        combination: ISlipPick[],
        system: SlipType,
        originalRewardToken: IRewardToken | null,
        overrides: IBetslipCalculatorOverrides,
    ): IRewardToken | null {
        const betOverride = this.getBetOverride(combination, system, overrides);
        if (betOverride?.rewardToken) {
            return betOverride.rewardToken.token;
        } else {
            return originalRewardToken;
        }
    }

    private getPickInfo(pickInfo: PickInfo, overrides: IBetslipCalculatorOverrides): PickInfo {
        const pickId = pickInfo.pick.id.toString();
        if (overrides.pickOverrides.replacements && pickId in overrides.pickOverrides.replacements) {
            pickInfo = overrides.pickOverrides.replacements[pickId]!; // We filter nulls in the begging.
        }

        return pickInfo;
    }

    /**
     * Get Pick EW place terms.
     *
     * @param pickInfo - betslip pick data
     * @param overrides - override data.
     * @private Returns PlaceTerms if pick is set to EW otherwise NULL.
     */
    private getEachWayTerms(pickInfo: PickInfo, overrides: IBetslipCalculatorOverrides): EachWayInfo | null {
        const pickId = pickInfo.pick.id.toString();
        if (overrides.pickOverrides.eachWay && pickId in overrides.pickOverrides.eachWay) {
            // Has override for this pickId. If the override is true then return pick place terms if false then return null.
            if (overrides.pickOverrides.eachWay[pickId]) {
                return overrides.pickOverrides.eachWay[pickId];
            }

            return null;
        }
        if (pickInfo.eachWayInfo) {
            // Only V2 Win Participant picks can be set to EW and the market should have
            return pickInfo.eachWayInfo;
        }

        return null;
    }

    /**
     * Get Pick price.
     *
     * @param pickInfo: Pick Info
     * @param isEachWay: Do we need the Each Way odds for this pick.
     * @param overrides
     * @private
     */
    private getPickPrice(pickInfo: PickInfo, isEachWay: boolean, overrides: IBetslipCalculatorOverrides): IBasePrice {
        const pickId = pickInfo.pick.id.toString();
        const price =
            overrides.pickOverrides.prices && pickId in overrides.pickOverrides.prices ? overrides.pickOverrides.prices[pickId] : pickInfo.price;
        if (!price) {
            return {
                type: PriceType.NoPrice,
                nativeOdds: emptyOdds,
            };
        }

        return { ...price };
    }

    private getEachWayPrice(slipPick: ISlipPick): ICalculatedPrice {
        if (slipPick.price.type !== PriceType.Fixed) {
            return {
                type: slipPick.price.type,
                odds: emptyCalculatedOdds,
            };
        }
        if (slipPick.eachWayInfo) {
            return {
                type: slipPick.price.type,
                odds: getEachWayOdds(slipPick.price, slipPick.eachWayInfo.placeTerms),
            };
        } else {
            // Here I suppose we can throw exception that pick is marked as EW without having each way terms.
            throw new Error('Each way pick without EachWay place terms');
        }
    }

    private updateSlipByBetsStatus(slip: ISlip, restrictions: CombinationRestrictions): void {
        if (slip.hasError) {
            return;
        }

        const { validBets, invalidBets } = this.splitBetsByValidity(slip.bets, restrictions);

        if (invalidBets) {
            slip.hasError = true;

            invalidBets.forEach((invalidBet) => (invalidBet.isValid = false));

            // If there are invalid bets and the legs in the valid bets are less than the picks in the slip
            // do not return any valid bets
            const legsCountInValidBets = this.getLegsCountInBets(validBets);
            if (legsCountInValidBets !== Object.keys(slip.slipPicks).length) {
                validBets?.forEach((invalidBet) => (invalidBet.isValid = false));
            }
        }
    }

    private splitBetsByValidity(bets: IBet[], restrictions: CombinationRestrictions): { validBets?: IBet[]; invalidBets?: IBet[] } {
        const invalidToken = 'invalid';
        const validToken = 'valid';

        const result = groupBy(bets, (bet) => {
            const legIds = bet.legs.map((leg) => leg.pickId.toString());

            for (const legId of legIds) {
                if (legId in restrictions.notCombinablePicks && legIds.some((l) => l in restrictions.notCombinablePicks[legId])) {
                    return invalidToken;
                }
            }

            for (const minimumCombinations of Object.values(restrictions.minimumCombinations)) {
                if (bet.legs.length < minimumCombinations) {
                    return invalidToken;
                }
            }

            return validToken;
        });

        return { validBets: result[validToken], invalidBets: result[invalidToken] };
    }

    private getLegsCountInBets(bets?: IBet[]): number {
        if (!bets?.length) {
            return 0;
        }

        const legsInValidBets = bets.reduce((legs: string[], bet: IBet) => {
            bet.legs.forEach((leg) => {
                if (legs.indexOf(leg.pickId.toString()) < 0) {
                    legs.push(leg.pickId.toString());
                }
            });

            return legs;
        }, []);

        return legsInValidBets.length;
    }
}
